import { UAParser } from 'ua-parser-js';

import { APP_ID } from '@/constants';
import { getTouristDeviceIdForRequest, getServerDeviceIdForRequest } from '@/utils/device/server-device-id';

import packageJson from '../../../package.json';

/** ua-parser-js 的 ESM 仅有命名导出 `UAParser`，无 default，须使用 `import { UAParser }`。 */
export interface IH5CommonParams {
    language?: string;
    app_id: number;
    device_id?: string;
    region?: string;
    platform: string;
    os_name?: string;
    os_version?: string;
    app_version?: string;
    update_version_code?: number;
    device_model?: string;
    brand?: string;
    time_zone?: string;
    channel?: string;
}

/** 由 app_version 去掉 `.` 后的纯数字组成，如 `1.1.3` → `113`（类型为 number） */
function appVersionToUpdateVersionCode(appVersion: string): number | undefined {
    const core = appVersion.trim().split('-')[0].trim();
    if (!core) {
        return undefined;
    }
    const merged = core.split('.').join('');
    if (!/^\d+$/.test(merged)) {
        return undefined;
    }
    const n = Number(merged);
    return Number.isFinite(n) ? n : undefined;
}

// 通参优先取正式登陆后的device_id，如果没有则取游客device_id
async function getDeviceId() {
    return (await getServerDeviceIdForRequest()) ?? (await getTouristDeviceIdForRequest());
}

function pickDefinedH5Params(obj: IH5CommonParams): Partial<IH5CommonParams> {
    const filtered = Object.entries(obj).filter(([, value]) => {
        if (value === undefined || value === null) {
            return false;
        }
        if (typeof value === 'string' && value.trim() === '') {
            return false;
        }
        return true;
    });
    return Object.fromEntries(filtered) as Partial<IH5CommonParams>;
}

function inferRegionFromNavigatorLanguage(): string | undefined {
    if (typeof navigator === 'undefined') {
        return undefined;
    }
    try {
        const loc = new Intl.Locale(navigator.language);
        return loc.region ?? undefined;
    } catch {
        return undefined;
    }
}

function uaField(value: string | undefined): string | undefined {
    if (value === undefined || value === null) {
        return undefined;
    }
    const t = String(value).trim();
    return t === '' ? undefined : t;
}

/** 基于 UA 字符串解析 os / device（与后端 CommonParam 命名对齐）；WebView 泛化 UA 时仍可能缺字段 */
function parseUaForCommonParams(
    ua: string
): Pick<IH5CommonParams, 'os_name' | 'os_version' | 'brand' | 'device_model'> {
    const empty: Pick<IH5CommonParams, 'os_name' | 'os_version' | 'brand' | 'device_model'> = {};
    if (!ua) {
        return empty;
    }

    try {
        const { os, device } = new UAParser(ua).getResult();
        const osNameRaw = uaField(os.name);
        let os_name: string;
        if (!osNameRaw) {
            os_name = 'Other';
        } else if (osNameRaw === 'Mac OS') {
            os_name = 'macOS';
        } else {
            os_name = osNameRaw;
        }

        return {
            os_name,
            os_version: uaField(os.version),
            brand: uaField(device.vendor),
            device_model: uaField(device.model),
        };
    } catch {
        return { os_name: 'Other' };
    }
}

interface INavigatorUaData {
    getHighEntropyValues?: (hints: string[]) => Promise<{
        model?: string;
        platformVersion?: string;
        fullVersionList?: unknown;
    }>;
}

async function enrichFromUserAgentData(
    base: Pick<IH5CommonParams, 'os_name' | 'os_version' | 'brand' | 'device_model'>
): Promise<Pick<IH5CommonParams, 'os_name' | 'os_version' | 'brand' | 'device_model'>> {
    if (typeof navigator === 'undefined') {
        return base;
    }
    const ud = (navigator as Navigator & { userAgentData?: INavigatorUaData }).userAgentData;
    if (!ud || typeof ud.getHighEntropyValues !== 'function') {
        return base;
    }

    try {
        const hints = await ud.getHighEntropyValues(['model', 'platformVersion', 'fullVersionList']);
        const platformVersion = uaField(hints.platformVersion);
        const model = typeof hints.model === 'string' && hints.model !== '' ? hints.model : undefined;
        return {
            ...base,
            os_version: platformVersion ?? base.os_version,
            device_model: model ?? base.device_model,
        };
    } catch {
        return base;
    }
}

export function getChannel(): string {
    if (process.env.ENV === 'prod') {
        return 'web';
    }
    if (process.env.ENV === 'test') {
        return 'local_test';
    }
    if (process.env.ENV === 'dev') {
        return 'local_dev';
    }
    return 'unknown';
}

/**
 * 组装 H5 端可稳定提供的 CommonParam 子集（服务端仍应优先用 IP 等推断 region）。
 */
export async function buildH5CommonParams(): Promise<Partial<IH5CommonParams>> {
    const device_id = (await getDeviceId()) ?? undefined;
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const fromUa = parseUaForCommonParams(ua);
    const fromUaCh = typeof window !== 'undefined' ? await enrichFromUserAgentData(fromUa) : fromUa;

    const app_version = packageJson.version;
    const update_version_code = appVersionToUpdateVersionCode(app_version);
    const params = new URLSearchParams(window.location.search);
    const ttclid = params.get('ttclid');
    const fbclid = params.get('fbclid');

    // mock 这里复用短剧的版本
    // const app_version = '1.1.7';
    // const update_version_code = 11702;

    const raw: IH5CommonParams = {
        app_id: Number(APP_ID),
        platform: 'Web',
        ...(device_id ? { device_id } : {}),
        language: typeof navigator !== 'undefined' ? navigator.language : undefined,
        region: inferRegionFromNavigatorLanguage(),
        time_zone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
        app_version,
        ...(Number.isFinite(update_version_code) ? { update_version_code } : {}),
        channel: getChannel(),
        ...fromUaCh,
        ...(ttclid ? { ttclid } : {}),
        ...(fbclid ? { fbclid } : {}),
    };

    return pickDefinedH5Params(raw);
}
